import { useCallback, useSyncExternalStore } from "react";
import type { Category, Habit, HabitId } from "../../../shared/types";
import { createHabitId } from "../../../shared/types";
import { getToday } from "../../../shared/types/date-helpers";
import {
  syncCreateHabit,
  syncDeleteHabit,
  syncToggleHabitDate,
  syncUpdateHabit,
  getPendingHabitIds,
} from "../../../lib/api/db-sync";
import { incrementHabitCompleted } from "../../gamification/store/gamification-store";

const STORAGE_KEY = "palnia_habits";

function loadHabits(): Habit[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as Array<Record<string, unknown>>).map(migrateHabit);
  } catch {
    return [];
  }
}

function migrateHabit(h: Record<string, unknown>): Habit {
  const result = {
    id: h.id as HabitId,
    title: (h.title as string) ?? "",
    category: (h.category as Category) ?? "personal",
    frequency: (h.frequency as "daily" | "weekly") ?? "daily",
    completedDates: Array.isArray(h.completedDates)
      ? (h.completedDates as string[])
      : [],
    createdAt: (h.createdAt as number) ?? Date.now(),
  };
  return result as Habit;
}

let habitsCache: Habit[] = loadHabits();
const listeners = new Set<() => void>();

function emitChange() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(habitsCache));
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  function handleStorageChange(event: StorageEvent) {
    if (event.key === STORAGE_KEY) {
      habitsCache = loadHabits();
      emitChange();
    }
  }

  window.addEventListener("storage", handleStorageChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorageChange);
  };
}

export function resetFromServer(rawHabits: unknown[]): void {
  const serverHabits = (rawHabits as Array<Record<string, unknown>>).map((h) =>
    migrateHabit({
      ...h,
      // Server returns ISO string, store expects timestamp number
      createdAt:
        typeof h.createdAt === "string"
          ? new Date(h.createdAt).getTime()
          : h.createdAt,
    }),
  );
  const serverIds = new Set(serverHabits.map((h) => h.id));
  const pendingIds = getPendingHabitIds();

  // Preserve local habits that are pending sync (not yet confirmed by server)
  const pendingLocal = habitsCache.filter(
    (h) => pendingIds.has(h.id) && !serverIds.has(h.id),
  );

  habitsCache = [...serverHabits, ...pendingLocal];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(habitsCache));
  for (const listener of listeners) { try { listener(); } catch { /* stale */ } }
}

function getSnapshot(): Habit[] {
  return habitsCache;
}

function addHabit(
  title: string,
  category: Category,
  frequency: "daily" | "weekly",
) {
  const habit: Habit = {
    id: createHabitId(),
    title,
    category,
    frequency,
    completedDates: [],
    createdAt: Date.now(),
  };
  habitsCache = [habit, ...habitsCache];
  emitChange();
  syncCreateHabit(habit);
}

function deleteHabit(id: HabitId) {
  habitsCache = habitsCache.filter((h) => h.id !== id);
  emitChange();
  syncDeleteHabit(id);
}

function toggleHabitDate(id: HabitId, date: string) {
  const habit = habitsCache.find((h) => h.id === id);
  const wasCompleted = habit?.completedDates.includes(date) ?? false;

  habitsCache = habitsCache.map((h) => {
    if (h.id !== id) return h;
    const dates = [...h.completedDates];
    const idx = dates.indexOf(date);
    if (idx >= 0) {
      dates.splice(idx, 1);
    } else {
      dates.push(date);
    }
    return { ...h, completedDates: dates };
  });
  emitChange();
  syncToggleHabitDate(id, date);

  if (!wasCompleted) {
    incrementHabitCompleted();
  }
}

function updateHabit(
  id: HabitId,
  updates: Partial<Pick<Habit, "title" | "category" | "frequency">>,
) {
  habitsCache = habitsCache.map((h) => {
    if (h.id !== id) return h;
    return { ...h, ...updates };
  });
  emitChange();
  syncUpdateHabit(id, updates);
}

function prevDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getStreak(id: HabitId): number {
  const habit = habitsCache.find((h) => h.id === id);
  if (!habit) return 0;

  const today = getToday();
  // Si aujourd'hui n'est pas coché, on commence depuis hier
  let current = habit.completedDates.includes(today) ? today : prevDay(today);
  let streak = 0;

  while (habit.completedDates.includes(current)) {
    streak++;
    current = prevDay(current);
  }

  return streak;
}

function nextDay(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getBestStreak(id: HabitId): number {
  const habit = habitsCache.find((h) => h.id === id);
  if (!habit || habit.completedDates.length === 0) return 0;

  const sorted = [...habit.completedDates].sort();
  let best = 1;
  let current = 1;

  for (let i = 1; i < sorted.length; i++) {
    if (nextDay(sorted[i - 1]) === sorted[i]) {
      current++;
      if (current > best) best = current;
    } else {
      current = 1;
    }
  }

  return best;
}

export function useHabits() {
  const habits = useSyncExternalStore(subscribe, getSnapshot);

  return {
    habits,
    addHabit: useCallback(
      (title: string, category: Category, frequency: "daily" | "weekly") =>
        addHabit(title, category, frequency),
      [],
    ),
    updateHabit: useCallback(
      (id: HabitId, updates: Partial<Pick<Habit, "title" | "category" | "frequency">>) =>
        updateHabit(id, updates),
      [],
    ),
    deleteHabit: useCallback((id: HabitId) => deleteHabit(id), []),
    toggleHabitDate: useCallback(
      (id: HabitId, date: string) => toggleHabitDate(id, date),
      [],
    ),
    getStreak: useCallback((id: HabitId) => getStreak(id), []),
    getBestStreak: useCallback((id: HabitId) => getBestStreak(id), []),
  };
}
